import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type Level,
  type DataType,
  type UiSize,
  type Origin,
} from './cheatsheetData';

export type FilterState = {
  search: string;
  level: 'all' | Level;
  dataType: 'all' | DataType;
  uiSize: 'all' | UiSize;
  origin: 'all' | Origin;
};

export const DEFAULT_FILTERS: FilterState = {
  search: '',
  level: 'all',
  dataType: 'all',
  uiSize: 'all',
  origin: 'all',
};

function isDefault(filters: FilterState) {
  return (
    filters.search === '' &&
    filters.level === 'all' &&
    filters.dataType === 'all' &&
    filters.uiSize === 'all' &&
    filters.origin === 'all'
  );
}

type Props = {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  resultCount: number;
  totalCount: number;
};

export function CheatsheetFilters({
  filters,
  onChange,
  resultCount,
  totalCount,
}: Props) {
  const dirty = !isDefault(filters);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Search…"
          className="h-8 w-44 pl-8 text-xs"
          spellCheck={false}
          autoComplete="off"
        />
      </div>

      {/* Level */}
      <Select
        value={filters.level}
        onValueChange={(v) =>
          onChange({ ...filters, level: v as FilterState['level'] })
        }
      >
        <SelectTrigger className="h-8 w-36 text-xs">
          <SelectValue placeholder="Level" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-xs">
            All levels
          </SelectItem>
          <SelectItem value="beginner" className="text-xs">
            Beginner
          </SelectItem>
          <SelectItem value="intermediate" className="text-xs">
            Intermediate
          </SelectItem>
          <SelectItem value="expert" className="text-xs">
            Expert
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Data type */}
      <Select
        value={filters.dataType}
        onValueChange={(v) =>
          onChange({ ...filters, dataType: v as FilterState['dataType'] })
        }
      >
        <SelectTrigger className="h-8 w-32 text-xs">
          <SelectValue placeholder="Data type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-xs">
            All types
          </SelectItem>
          <SelectItem value="number" className="text-xs">
            Number
          </SelectItem>
          <SelectItem value="string" className="text-xs">
            String
          </SelectItem>
          <SelectItem value="boolean" className="text-xs">
            Boolean
          </SelectItem>
          <SelectItem value="array" className="text-xs">
            Array
          </SelectItem>
          <SelectItem value="range" className="text-xs">
            Range
          </SelectItem>
          <SelectItem value="special" className="text-xs">
            Special
          </SelectItem>
        </SelectContent>
      </Select>

      {/* UI size */}
      <Select
        value={filters.uiSize}
        onValueChange={(v) =>
          onChange({ ...filters, uiSize: v as FilterState['uiSize'] })
        }
      >
        <SelectTrigger className="h-8 w-28 text-xs">
          <SelectValue placeholder="UI size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-xs">
            All sizes
          </SelectItem>
          <SelectItem value="compact" className="text-xs">
            Compact
          </SelectItem>
          <SelectItem value="large" className="text-xs">
            Large
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Origin */}
      <Select
        value={filters.origin}
        onValueChange={(v) =>
          onChange({ ...filters, origin: v as FilterState['origin'] })
        }
      >
        <SelectTrigger className="h-8 w-40 text-xs">
          <SelectValue placeholder="Origin" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-xs">
            All origins
          </SelectItem>
          <SelectItem value="native" className="text-xs">
            OpenSCAD Native
          </SelectItem>
          <SelectItem value="meshpit" className="text-xs">
            Meshpit Extension
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Reset */}
      {dirty && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => onChange(DEFAULT_FILTERS)}
        >
          <X className="h-3.5 w-3.5" />
          Reset
        </Button>
      )}

      {/* Count */}
      <span className="ml-auto text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{resultCount}</span>
        {' / '}
        <span className="font-medium text-foreground">{totalCount}</span>
      </span>
    </div>
  );
}
